import React, {Component} from 'react';
import {Button, Input, message, Select, Tooltip} from 'antd';
import {AudioOutlined} from '@ant-design/icons';
import {connect} from 'react-redux';
import {IAppState} from '../../../redux/store';
import selectors from '../../../redux/selectors';
import CountryFlag from '../display/CountryFlag';
import {SizeType} from 'antd/es/config-provider/SizeContext';

export interface ISpeechLanguage {
    code: string;
    countryCode: string;
    name: string;
}

const DEFAULT_LANGUAGES: ISpeechLanguage[] = [
    {code: 'sk-SK', countryCode: 'sk', name: 'Slovenčina'},
    {code: 'cs-CZ', countryCode: 'cz', name: 'Čeština'},
    {code: 'en-US', countryCode: 'us', name: 'English (US)'},
    {code: 'en-GB', countryCode: 'gb', name: 'English (UK)'},
    {code: 'de-DE', countryCode: 'de', name: 'Deutsch'},
    {code: 'pl-PL', countryCode: 'pl', name: 'Polski'},
    {code: 'uk-UA', countryCode: 'ua', name: 'Українська'},
];

interface IProps {
    value?: string;
    onChange?: (value: string) => void;
    onAppend?: (transcribedText: string) => void;
    defaultLanguage?: string;
    silenceTimeoutMs?: number;
    showVolumeBar?: boolean;
    size?: SizeType;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    getText: (code: string, language: string, fallback?: string) => string;
}

interface IState {
    isListening: boolean;
    selectedLanguage: string;
    volumeLevel: number;
}

class SpeechToTextInput extends Component<IProps, IState> {
    private recognition: any = null;
    private shouldKeepListening: boolean = false;
    private silenceTimer: any = null;
    private hasSpokenInSession: boolean = false;

    // Web Audio API
    private audioContext: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private mediaStream: MediaStream | null = null;
    private animFrameId: number | null = null;

    // The single source of truth for text accumulated during the mic session
    private accumulatedText: string = '';
    private currentInterim: string = '';

    static defaultProps = {
        silenceTimeoutMs: 3000,
        showVolumeBar: true
    };

    constructor(props: IProps) {
        super(props);

        const detectedLocale = (navigator.language || '').toLowerCase();
        let initialLang = props.defaultLanguage || 'sk-SK';
        if (!props.defaultLanguage) {
            if (detectedLocale.startsWith('cs')) initialLang = 'cs-CZ';
            else if (detectedLocale.startsWith('en')) initialLang = 'en-US';
            else if (detectedLocale.startsWith('de')) initialLang = 'de-DE';
        }

        this.state = {
            isListening: false,
            selectedLanguage: initialLang,
            volumeLevel: 0
        };
    }

    componentDidMount() {
        this.initSpeechRecognition();
    }

    componentWillUnmount() {
        this.clearSilenceTimer();
        this.stopAudioAnalysis();
        this.stopRecognition();
    }

    private clearSilenceTimer = () => {
        if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
        }
    };

    private resetSilenceTimer = () => {
        this.clearSilenceTimer();
        if (!this.shouldKeepListening || !this.hasSpokenInSession) return;

        const timeout = this.props.silenceTimeoutMs || 3000;
        this.silenceTimer = setTimeout(() => {
            this.stopRecognition();
        }, timeout);
    };

    private initSpeechRecognition = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) return;

        const recognition = new SpeechRecognition();
        // Disabling native continuous mode fixes the duplication bug
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = this.state.selectedLanguage;

        recognition.onstart = () => {
            this.hasSpokenInSession = false;
            this.setState({isListening: true});
            this.startAudioAnalysis();
        };

        recognition.onsoundstart = () => {
            this.hasSpokenInSession = true;
            this.resetSilenceTimer();
        };

        recognition.onspeechstart = () => {
            this.hasSpokenInSession = true;
            this.resetSilenceTimer();
        };

        recognition.onresult = (event: any) => {
            this.hasSpokenInSession = true;
            this.resetSilenceTimer();

            let phraseFinal = '';
            let phraseInterim = '';

            for (let i = 0; i < event.results.length; ++i) {
                const item = event.results[i];
                const text = item[0]?.transcript || '';
                if (item.isFinal) {
                    phraseFinal += text;
                } else {
                    phraseInterim += text;
                }
            }

            if (phraseFinal) {
                const trimmedFinal = phraseFinal.trim();
                if (trimmedFinal) {
                    this.accumulatedText = (this.accumulatedText ? this.accumulatedText + ' ' : '') + trimmedFinal;
                    this.currentInterim = '';
                    this.props.onAppend?.(trimmedFinal);
                }
            } else {
                this.currentInterim = phraseInterim.trim();
            }

            // Emit clean combined value
            if (this.props.onChange) {
                const fullText = [this.accumulatedText, this.currentInterim].filter(Boolean).join(' ').trim();
                this.props.onChange(fullText);
            }
        };

        recognition.onerror = (event: any) => {
            if (event.error === 'no-speech') return;
            console.error('Speech recognition error:', event.error);
            if (event.error === 'not-allowed') {
                const {getText} = this.props;
                message.error(getText('system.input.speech-to-text.denied', 'Prístup k mikrofónu bol zamietnutý.'));
                this.stopRecognition();
            }
        };

        recognition.onend = () => {
            // If the user didn't hit stop, spin up the next clean recognition window
            if (this.shouldKeepListening) {
                try {
                    this.recognition?.start();
                } catch (e) {
                    // Ignore transient restart collisions
                }
            } else {
                this.clearSilenceTimer();
                this.stopAudioAnalysis();
                this.setState({isListening: false, volumeLevel: 0});
            }
        };

        this.recognition = recognition;
    };

    private startAudioAnalysis = async () => {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isMobile) {
            let step = 0;
            const simulateVolume = () => {
                if (!this.state.isListening) return;
                step += 0.15;
                const wave = Math.sin(step) * 25 + 35;
                this.setState({volumeLevel: Math.round(wave)});
                this.animFrameId = requestAnimationFrame(simulateVolume);
            };
            this.animFrameId = requestAnimationFrame(simulateVolume);
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            this.mediaStream = stream;

            const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
            this.audioContext = new AudioCtx();
            const source = this.audioContext.createMediaStreamSource(stream);
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            this.analyser.smoothingTimeConstant = 0.4;
            source.connect(this.analyser);

            const bufferLength = this.analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const checkVolume = () => {
                if (!this.analyser || !this.state.isListening) {
                    this.setState({volumeLevel: 0});
                    return;
                }

                this.analyser.getByteFrequencyData(dataArray);

                let sum = 0;
                for (let i = 0; i < bufferLength; i++) {
                    sum += dataArray[i];
                }
                const avg = sum / bufferLength;
                const normalized = Math.min(100, Math.round((avg / 64) * 100));

                this.setState({volumeLevel: normalized});
                this.animFrameId = requestAnimationFrame(checkVolume);
            };

            this.animFrameId = requestAnimationFrame(checkVolume);
        } catch (err) {
            console.warn('Microphone volume meter unavailable:', err);
        }
    };

    private stopAudioAnalysis = () => {
        if (this.animFrameId) {
            cancelAnimationFrame(this.animFrameId);
            this.animFrameId = null;
        }
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close().catch(() => {});
            this.audioContext = null;
        }
        this.analyser = null;
    };

    private toggleListening = () => {
        const {getText} = this.props;
        if (!this.recognition) {
            message.warning(getText('system.input.speech-to-text.unsupported', 'Prohlížeč nepodporuje rozpoznávání hlasu (používáte Chrome, Edge nebo Safari).'));
            return;
        }

        if (this.state.isListening) {
            this.stopRecognition();
        } else {
            this.startRecognition();
        }
    };

    private startRecognition = () => {
        if (!this.recognition) return;

        // Initialize accumulatedText from props.value if present, otherwise empty
        this.accumulatedText = (this.props.value || '').trim();
        this.currentInterim = '';
        this.hasSpokenInSession = false;
        this.shouldKeepListening = true;
        this.recognition.lang = this.state.selectedLanguage;

        try {
            this.recognition.start();
        } catch (e) {
            console.warn('Recognition start warning:', e);
        }
    };

    private stopRecognition = () => {
        this.shouldKeepListening = false;
        this.hasSpokenInSession = false;
        this.clearSilenceTimer();
        this.stopAudioAnalysis();

        if (this.recognition) {
            try {
                this.recognition.stop();
            } catch (e) {}
        }

        // Commit final clean accumulated text (dropping any in-flight interim guesses)
        if (this.props.onChange) {
            this.props.onChange(this.accumulatedText.trim());
        }

        this.currentInterim = '';
        this.setState({isListening: false, volumeLevel: 0});
    };

    private onLanguageChange = (langCode: string) => {
        this.setState({selectedLanguage: langCode}, () => {
            if (this.recognition) {
                this.recognition.lang = langCode;
                if (this.state.isListening) {
                    this.recognition.stop();
                }
            }
        });
    };

    render() {
        const {isListening, selectedLanguage, volumeLevel} = this.state;
        const {size, disabled, className, style, showVolumeBar, getText} = this.props;

        return (
            <div
                className={`speech-to-text-control ${className || ''}`}
                style={{display: 'inline-flex', alignItems: 'center', gap: 6, ...style}}
            >
                <Tooltip title={isListening ? getText('system.input.speech-to-text.stop', 'Zastavit') : getText('system.input.speech-to-text.start', 'Mluvit')}>
                    <Input.Group compact={true} style={{display: 'inline-flex', alignItems: 'center'}}>
                        <Button
                            type={isListening ? 'primary' : 'default'}
                            danger={isListening}
                            icon={<AudioOutlined className={isListening ? 'ant-btn-icon-recording' : ''}/>}
                            onClick={this.toggleListening}
                            size={size}
                            disabled={disabled}
                        />

                        {isListening && showVolumeBar ? (
                            <Button
                                style={{
                                    padding: '0 6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    backgroundColor: '#fff1f0',
                                    borderTop: '1px solid #ffccc7',
                                    borderBottom: '1px solid #ffccc7',
                                    zIndex: 2,
                                }}
                            >
                                {[0.6, 1.2, 0.9, 0.5].map((factor, i) => {
                                    const barHeight = Math.min(20, Math.max(3, Math.round((volumeLevel * factor) / 4)));
                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                width: 3,
                                                height: `${barHeight}px`,
                                                backgroundColor: '#ff4d4f',
                                                borderRadius: 2,
                                                transition: 'height 0.06s ease'
                                            }}
                                        />
                                    );
                                })}
                            </Button>
                        ) : (
                            <Select
                                size={size}
                                disabled={disabled || isListening}
                                value={selectedLanguage}
                                onChange={this.onLanguageChange}
                                dropdownMatchSelectWidth={false}
                            >
                                {DEFAULT_LANGUAGES.map(lang => (
                                    <Select.Option key={lang.code} value={lang.code} title={lang.name}>
                                        <CountryFlag countryCode={lang.countryCode}/>
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    </Input.Group>
                </Tooltip>
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => ({
    getText: (code: string, language: string, fallback?: string) =>
        selectors.dictionary.getMessage(state, code, fallback, {}, language),
});

export default connect(mapStateToProps)(SpeechToTextInput);